import React, { useState, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Box, Typography, Collapse, Paper, Divider, Tooltip } from '@mui/material';
import { InfoOutlined, ExpandMore, ExpandLess, Description, Image as ImageIcon } from '@mui/icons-material';

import { WrappedRCEMessageBox } from './wrapped/WrappedRCEMessageBox';
import { imageUrlToDisplayState } from '../../state/chat/ImageUrlToDisplayState';
import { chatbotConfigState } from '../../state/config/ChatbotConfigState';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { sendMessageToChatbot, useChatState } from '../../utils/client/messageUtils';

function normalizeChatTextForDisplay(input) {
    let value = String(input || '');

    // 1. Remove markdown bold/italic/code markers entirely
    value = value.replace(/[*_`]/g, '');

    // 2. Remove leading spaces from every line in case the AI is indenting the text
    value = value.split('\n').map(line => line.trimStart()).join('\n');

    return value;
}

function normalizeSource(source) {
    if (typeof source === 'string') {
        return { filename: source, fileType: '', score: null, snippet: '' };
    }
    return source || { filename: '', fileType: '', score: null, snippet: '' };
}

function useTypewriter(text, isActive) {
    const safeText = String(text || '');
    const shouldAnimate = isActive && safeText.length <= 1200;
    const [index, setIndex] = useState(() => (shouldAnimate ? 0 : safeText.length));

    useEffect(() => {
        if (!shouldAnimate) {
            setIndex(safeText.length);
            return;
        }

        setIndex(0);
        const speed = safeText.length > 400 ? 8 : 15;

        const timer = setInterval(() => {
            setIndex((prevIndex) => {
                if (prevIndex >= safeText.length) {
                    clearInterval(timer);
                    return prevIndex;
                }
                return prevIndex + 1;
            });
        }, speed);

        return () => clearInterval(timer);
    }, [safeText, shouldAnimate]);

    return safeText.substring(0, index);
}

export function ChatMessageBox({ message, isLatest }) {
    const setImageDialogUrl = useSetAtom(imageUrlToDisplayState);
    const config = useAtomValue(chatbotConfigState);
    const { t } = useAppTranslation();
    const chatState = useChatState();
    const [showSources, setShowSources] = useState(false);

    function handleImageClick(url) {
        setImageDialogUrl(url);
    }

    function toggleSources() {
        setShowSources((prev) => !prev);
    }

    function handleSourceKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleSources();
        }
    }

    // Function to handle clicking on a specific source to ask the chatbot about it
    function handleSourceClick(source) {
        if (chatState.isLoading) return;
        
        const sourceDetails = normalizeSource(source);
        const queryText = `Tell me more about the source: ${sourceDetails.filename}`;
        sendMessageToChatbot(queryText, chatState);
    }

    const sender = message?.sender || message?.role || 'bot';
    const isUserMessage = sender === 'user';
    const shouldAnimate = sender === 'bot' && !!isLatest;
    const normalizedMessageText = normalizeChatTextForDisplay(message.message);
    const displayText = useTypewriter(normalizedMessageText, shouldAnimate);
    const hasSources = message.debugSources && message.debugSources.length > 0;

    return (
        <Box
            display='flex'
            flexDirection='column'
            alignItems={isUserMessage ? 'flex-end' : 'flex-start'}
            sx={{ 
                width: '100%',
                // Aggressively force left alignment on all possible text containers inside the bubble
                '& .rce-mbox, & .rce-mbox-body, & .rce-mbox-text, & .rce-mbox-text p, & .rce-mbox-text div': {
                    textAlign: 'left !important',
                }
            }}
        >
            <WrappedRCEMessageBox
                position={isUserMessage ? 'right' : 'left'}
                text={displayText}
                title={isUserMessage ? t.youLabel : config.chatbotName}
                media={message.media}
                date={message.sentAt || null}
                primaryColor={config.primaryColor || '#172983'}
                handleImageClick={handleImageClick}
            />

            {!isUserMessage && hasSources && (
                <Box sx={{ marginTop: 0.15, marginLeft: { xs: 0.75, sm: 1.25 }, marginBottom: 1.75, maxWidth: { xs: '88%', sm: '76%' } }}>
                    <Box
                        component='button'
                        type='button'
                        display='inline-flex'
                        alignItems='center'
                        gap={0.5}
                        onClick={toggleSources}
                        onKeyDown={handleSourceKeyDown}
                        aria-expanded={showSources}
                        sx={{
                            cursor: 'pointer',
                            color: 'text.secondary',
                            px: 1,
                            py: 0.5,
                            borderRadius: 99,
                            backgroundColor: 'rgba(255,255,255,0.86)',
                            border: '1px solid var(--border-color)',
                            appearance: 'none',
                            font: 'inherit',
                            boxShadow: '0 6px 14px rgba(15, 23, 42, 0.05)',
                            '&:hover': { color: 'primary.main', backgroundColor: 'rgba(255,255,255,0.95)' },
                            '&:focus-visible': {
                                outline: '2px solid var(--accent-color)',
                                outlineOffset: 2,
                            },
                        }}
                    >
                        <InfoOutlined sx={{ fontSize: 16 }} />
                        <Typography variant='caption' fontWeight='bold'>
                            {message.debugSources?.length} {message.debugSources?.length === 1 ? t.sourcesSingular : t.sourcesPlural}
                        </Typography>
                        {showSources ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                    </Box>

                    <Collapse in={showSources}>
                        <Paper
                            elevation={0}
                            sx={{
                                mt: 1,
                                p: 1.5,
                                borderRadius: 3,
                                backgroundColor: 'rgba(255,255,255,0.96)',
                                border: '1px solid var(--border-color)',
                                boxShadow: '0 10px 25px rgba(15, 23, 42, 0.06)',
                            }}
                        >
                            <Typography variant='caption' display='block' mb={1} color='text.secondary'>
                                {t.referencedDocuments}
                            </Typography>

                            {message.debugSources?.map((source, index) => (
                                <Box 
                                    key={index} 
                                    mb={index !== (message.debugSources?.length || 0) - 1 ? 1 : 0}
                                    onClick={() => handleSourceClick(source)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleSourceClick(source);
                                        }
                                    }}
                                    sx={{
                                        cursor: 'pointer',
                                        borderRadius: 2,
                                        p: 1,
                                        transition: 'all 0.2s',
                                        border: '1px solid transparent',
                                        '&:hover': {
                                            backgroundColor: 'var(--component-bg-light, rgba(0,0,0,0.03))',
                                            borderColor: 'var(--border-color)',
                                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)',
                                        },
                                        '&:focus-visible': {
                                            outline: '2px solid var(--accent-color)',
                                            outlineOffset: 2,
                                        }
                                    }}
                                >
                                    <Box display='flex' alignItems='center' gap={1}>
                                        {normalizeSource(source).fileType?.startsWith('image') ? (
                                            <ImageIcon color='action' fontSize='small' />
                                        ) : (
                                            <Description color='action' fontSize='small' />
                                        )}
                                        <Typography variant='subtitle2' sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                            {normalizeSource(source).filename}
                                            {normalizeSource(source).pageNumber ? ` · ${t.pageLabel || 'Page'} ${normalizeSource(source).pageNumber}` : ''}
                                        </Typography>
                                        {normalizeSource(source).score !== null && normalizeSource(source).score !== undefined && Number.isFinite(Number(normalizeSource(source).score)) && <Tooltip title={t.relevanceMatch}>
                                            <Box
                                                bgcolor={normalizeSource(source).score > 0.7 ? '#ecfdf3' : '#fff7ed'}
                                                color={normalizeSource(source).score > 0.7 ? '#15803d' : '#c2410c'}
                                                px={0.75}
                                                py={0.15}
                                                borderRadius={99}
                                                border='1px solid currentColor'
                                            >
                                                <Typography variant='caption' fontWeight='bold'>
                                                    {Math.round(normalizeSource(source).score * 100)}%
                                                </Typography>
                                            </Box>
                                        </Tooltip>}
                                    </Box>

                                    {normalizeSource(source).snippet && <Box
                                        ml={3.5}
                                        mt={0.75}
                                        p={1}
                                        bgcolor='var(--component-bg-light)'
                                        borderRadius={2}
                                        border='1px solid var(--border-color)'
                                    >
                                        <Typography variant='caption' color='text.secondary' sx={{ fontStyle: 'italic' }}>
                                            &ldquo;{normalizeChatTextForDisplay(normalizeSource(source).snippet)}&rdquo;
                                        </Typography>
                                    </Box>}

                                    {index < (message.debugSources?.length || 0) - 1 && <Divider sx={{ my: 1 }} />}
                                </Box>
                            ))}
                        </Paper>
                    </Collapse>
                </Box>
            )}
        </Box>
    );
}
